/**
 * WelcomeFeaturesPage - Step 3 of App Orientation Flow
 * 
 * Interactive walkthrough of key Sparvi features.
 * Shows dashboard navigation, data explorer, validation results, and issue management.
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { userAPI } from '../../api/enhancedApiService';
import { 
  HomeIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  CogIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  PlayIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import Logo from '../../components/common/Logo';

const WelcomeFeaturesPage = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState('dashboard');

  const features = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: HomeIcon,
      description: 'Your command center for data quality monitoring',
      details: [
        'Overview of all data health across connections',
        'Recent validation results and trends',
        'Quick access to critical issues',
        'Performance metrics and alerts'
      ],
      demoPath: '/dashboard',
      screenshot: 'dashboard-preview'
    },
    {
      id: 'explorer',
      name: 'Data Explorer',
      icon: MagnifyingGlassIcon,
      description: 'Dive deep into your data structure and quality',
      details: [
        'Browse databases, schemas, and tables',
        'View column details and data types',
        'Preview actual data samples',
        'Analyze data quality metrics'
      ],
      demoPath: '/explorer',
      screenshot: 'explorer-preview'
    },
    {
      id: 'validations',
      name: 'Validations',
      icon: ShieldCheckIcon,
      description: 'Automated data quality checks and rules',
      details: [
        'Create and manage validation rules',
        'Monitor rule execution results',
        'Set up automated alerts',
        'Track quality trends over time'
      ],
      demoPath: '/validations',
      screenshot: 'validations-preview'
    },
    {
      id: 'issues',
      name: 'Issue Management',
      icon: ExclamationTriangleIcon,
      description: 'Track and resolve data quality problems',
      details: [
        'Create issues for data quality problems',
        'Assign issues to team members',
        'Track resolution progress',
        'Document root causes and fixes'
      ],
      demoPath: '/issues',
      screenshot: 'issues-preview'
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: ChartBarIcon,
      description: 'Advanced insights and reporting',
      details: [
        'Data quality trends and patterns',
        'Table-level health analytics',
        'Schema change detection',
        'Business impact assessment'
      ],
      demoPath: '/analytics',
      screenshot: 'analytics-preview'
    }
  ];

  const activeFeatureData = features.find(f => f.id === activeFeature);

  const handleContinue = async () => {
    try {
      // Update progress in backend
      await userAPI.updateOnboardingProgress({
        current_step: 'features',
        steps_completed: ['welcome', 'data', 'features'],
        completion_rate: 60, // 3 of 5 steps
        features_explored: {
          features_viewed: features.map(f => f.id),
          active_feature: activeFeature,
          completed_at: new Date().toISOString()
        }
      });
      
      console.log('[WelcomeFeatures] Updated onboarding progress');
    } catch (error) {
      console.error('[WelcomeFeatures] Error tracking progress:', error);
      // Continue with navigation even if tracking fails
    }
    
    navigate('/welcome/tasks');
  };

  const handleBack = () => {
    navigate('/welcome/data');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Logo size="medium" withText={true} />
            <div className="text-sm text-gray-500">Welcome Tour • Step 3 of 5</div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full" style={{ width: '60%' }}></div>
            </div>
            <span className="ml-3 text-sm text-gray-600">3 of 5</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-full mb-4">
            <CogIcon className="w-6 h-6 text-white" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Master the Key Features
          </h1>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Let's walk through the main features you'll use daily to monitor, 
            explore, and maintain your data quality.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Feature Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Features</h2>
              <nav className="space-y-2">
                {features.map((feature) => {
                  const IconComponent = feature.icon;
                  return (
                    <button
                      key={feature.id}
                      onClick={() => setActiveFeature(feature.id)}
                      className={`w-full flex items-center p-3 rounded-lg text-left transition-colors ${
                        activeFeature === feature.id
                          ? 'bg-blue-50 border border-blue-200 text-blue-900'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <IconComponent className="w-5 h-5 mr-3 flex-shrink-0" />
                      <div>
                        <div className="font-medium">{feature.name}</div>
                        <div className="text-sm text-gray-500">{feature.description}</div>
                      </div>
                      {activeFeature === feature.id && (
                        <CheckIcon className="w-5 h-5 text-blue-600 ml-auto" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Feature Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border">
              {/* Feature Header */}
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      {React.createElement(activeFeatureData.icon, { className: "w-5 h-5 text-blue-600" })}
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">{activeFeatureData.name}</h2>
                      <p className="text-gray-600">{activeFeatureData.description}</p>
                    </div>
                  </div>
                  
                  <Link
                    to={activeFeatureData.demoPath}
                    className="inline-flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <PlayIcon className="w-4 h-4 mr-2" />
                    Try It Live
                  </Link>
                </div>
              </div>

              {/* Feature Preview */}
              <div className="p-6">
                {/* Mock Screenshot/Preview */}
                <div className="bg-gray-100 rounded-lg mb-6 overflow-hidden">
                  <div className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                    <div className="text-center">
                      {React.createElement(activeFeatureData.icon, { className: "w-16 h-16 text-gray-400 mx-auto mb-4" })}
                      <p className="text-gray-500 font-medium">{activeFeatureData.name} Preview</p>
                      <p className="text-sm text-gray-400">Interactive demo available in live app</p>
                    </div>
                  </div>
                </div>

                {/* Key Capabilities */}
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Capabilities</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {activeFeatureData.details.map((detail, index) => (
                    <div key={index} className="flex items-start">
                      <CheckIcon className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{detail}</span>
                    </div>
                  ))}
                </div>

                {/* Feature-specific Tips */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">
                    Pro Tip for {activeFeatureData.name}
                  </h4>
                  <p className="text-blue-800 text-sm">
                    {activeFeature === 'dashboard' && "Bookmark key metrics by clicking the star icon. They'll appear in your personalized quick-access panel."}
                    {activeFeature === 'explorer' && "Use the search functionality to quickly find tables. You can search by name, description, or even column names."}
                    {activeFeature === 'validations' && "Start with pre-built validation templates, then customize them as you learn more about your data patterns."}
                    {activeFeature === 'issues' && "Add context and screenshots when creating issues. It helps team members understand and resolve problems faster."}
                    {activeFeature === 'analytics' && "Set up email alerts for significant changes in data quality trends to stay proactive."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Access Reference */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation Reference</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            {features.map((feature) => {
              const IconComponent = feature.icon;
              return (
                <div key={feature.id} className="p-3 bg-gray-50 rounded-lg">
                  <IconComponent className="w-6 h-6 text-gray-600 mx-auto mb-2" />
                  <div className="text-sm font-medium text-gray-900">{feature.name}</div>
                  <div className="text-xs text-gray-500">{feature.demoPath}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back
          </button>
          
          <button
            onClick={handleContinue}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Practice with Tasks
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeFeaturesPage;